/**
 * 忘记密码 · 第 1 步：填邮箱，请求重置验证码。
 *
 * ## 为什么这里**不**区分"邮箱不存在"
 *
 * 后端无论邮箱是否存在都返回成功（防账号探测，见 `auth.service.requestPasswordReset`），
 * 所以这一页也必须一样：**找不到邮箱、没注册、被限流，用户看到的都是同一句**
 * "如果该邮箱已注册，我们已发送重置邮件"。任何"这个邮箱没注册"的提示都会把
 * 登录页变成一台账号探测器。
 *
 * ## 两步为什么拆成两个页面而不是一个页面的两个状态
 *
 * 因为第 2 步（`/reset`）需要一条**可以单独打开的**地址：用户在手机上收邮件、
 * 切到浏览器、甚至换一台设备打开，都只能靠 `/reset` 这一条 URL 落地。
 * 把两步塞进同一个组件、靠内存里的 `step` 变量切换，就做不到这件事。
 *
 * ⚠️ 这一页**不印任何命令行 / 运维信息**：家长不该知道 `operator.ts` 的存在，
 *    那既不解决问题（他登不上服务器），也会让人以为"出事了"。
 *    运维侧的口径见 `docs/接口契约对齐.md` 的「紧急恢复」。
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { emailSchema } from '@fht/shared';
import * as authApi from '../../lib/api/auth';
import { ApiError, toErrorMessage } from '../../lib/api/error';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { BuildStamp } from '../../components/layout/BuildStamp';

/** 与后端 `RESET_EMAIL_COOLDOWN_SECONDS` 一致：同一邮箱 60 秒内只发一封。 */
const RESEND_COOLDOWN_SECONDS = 60;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // 与后端同口径：先 trim 再校验。用户从通讯录粘过来常常带空格或大写，
    // 后端 emailSchema 也会 trim + 转小写，这里不先归一化就会出现
    // "前端说格式不对、后端其实能认"的口径差。
    const normalized = email.trim().toLowerCase();
    const parsed = emailSchema.safeParse(normalized);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? '请输入正确的邮箱地址');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.requestPasswordReset(parsed.data);
      navigate(`/reset?email=${encodeURIComponent(parsed.data)}`, { replace: true });
    } catch (err) {
      /**
       * 后端对"邮箱不存在"是静默成功的，所以走到这里的只有两类：
       *  - 发信被限流（同一邮箱 60 秒内重复点，或同一出口 IP 请求太密）；
       *  - 真的发不出去（邮件服务故障）。
       * 两类都不能说成"这个邮箱有问题" —— 那句话本身就是枚举信号。
       * 限流单独给一句更可操作的话，其余沿用后端的中文文案。
       */
      if (err instanceof ApiError && err.status === 429) {
        setError(`发送太频繁了，请等 ${RESEND_COOLDOWN_SECONDS} 秒后再试一次`);
      } else {
        setError(toErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900">找回密码</h1>
          <p className="mt-1 text-sm text-slate-500">
            输入注册邮箱，我们会发一封邮件，里面有 6 位验证码
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <Input
            label="注册邮箱"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="email"
            inputMode="email"
            placeholder="你注册时填的邮箱"
          />

          {error && (
            <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
              {error}
            </div>
          )}

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            发送重置邮件
          </Button>

          <p className="text-xs leading-relaxed text-slate-500">
            收不到？先看一眼<b>垃圾邮件箱</b>；同一邮箱 1 分钟内只能发一次。
          </p>

          <p className="text-center text-sm text-slate-500">
            <Link to="/login" className="font-medium text-brand-600 hover:underline">
              返回登录
            </Link>
          </p>
        </form>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          位置与其它 4 个未登录页一致：居中卡片（这里是表单本身）**下方的页脚处居中**。
          复用全站唯一的 `BuildStamp`（见 lib/buildStamp.ts）——不另写实现、不复制样式。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}
